import React, { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Search, Package, ChevronDown, ChevronRight, ListTree } from 'lucide-react'
import { toast } from 'sonner'
import z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

// Componentes UI Globales
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { FormError } from '../components/ui/FormError'
import { SearchFilter } from '../components/ui/SearchFilter'
import { DiscreteLoader } from '../components/ui/discrete-loader'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog'
import { useSessionStore } from '../store/auth/auth.store'

// Cliente HTTP y Servicio
import { productTypeService } from '../services/product-type.service'

// ─── 1. Tipos de Datos (Interfaces) ──────────────────────────────────────────
export interface ProductTypeItem {
  id: number
  name: string
  code: string
  description: string
  requires_lot: number
  requires_prescription: number
  regulatory_class?: 'ANTIBIOTIC' | 'CONTROLLED' | null
  parent_id?: number | null
  children?: ProductTypeItem[]
}

// ─── 2. Esquema de Validación (Zod) ──────────────────────────────────────────
const formSchema = z.object({
  name: z.string().min(3, 'El nombre es obligatorio'),
  code: z.string().min(3, 'El código es obligatorio'),
  description: z.string().optional(),
  requires_lot: z.boolean(),
  requires_prescription: z.boolean(),
  parent_id: z.number().nullable().optional()
})

type FormValidate = z.infer<typeof formSchema>

// ─── 3. Componente Principal ───────────────────────────────────────────────
export function ProductTypes() {
  // ─── Estados (Variables Reactivas) ───
  const [data, setData] = useState<ProductTypeItem[]>([])
  const [paginationMeta, setPaginationMeta] = useState<any>({ total: 0, from: 0, to: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [refreshCount, setRefreshCount] = useState(0)
  const [expandedRows, setExpandedRows] = useState<number[]>([])

  const toggleRow = (id: number) => {
    setExpandedRows(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id])
  }

  // Control de Modales
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  const canCreateProductType = useSessionStore((s) => s.canAction('inventory.product-type.create'))
  const canEditProductType = useSessionStore((s) => s.canAction('inventory.product-type.edit'))
  const canDeleteProductType = useSessionStore((s) => s.canAction('inventory.product-type.delete'))

  // Paginación
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // ─── Formulario (React Hook Form) ───
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<FormValidate>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', code: '', description: '', requires_lot: false, requires_prescription: false, parent_id: null }
  })

  // ─── Efecto: Cargar Datos de la API ───
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await productTypeService.getProductTypes({
          search: searchQuery,
          page: currentPage,
          per_page: itemsPerPage
        })
        let items = response.data?.data || response.data || response || []

        setData(items)

        // Guardar metadatos de paginación del backend (Laravel)
        setPaginationMeta(response.data || response.meta || response || { total: 0, from: 0, to: 0 })
      } catch (error) {
        toast.error('Error al cargar los datos')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [refreshCount, searchQuery, currentPage])

  // ─── Lógica de Paginación ───
  // Los datos ya vienen filtrados y paginados del backend
  const paginatedData = data

  // ─── Funciones de Interfaz ───
  const openCreateModal = () => {
    if (!canCreateProductType) return
    setEditingId(null)
    reset({ name: '', code: '', description: '', requires_lot: false, requires_prescription: false, parent_id: null })
    setShowModal(true)
  }

  const openEditModal = (item: ProductTypeItem) => {
    if (!canEditProductType) return
    setEditingId(item.id)
    reset({
      name: item.name,
      code: item.code,
      description: item.description || '',
      requires_lot: Number(item.requires_lot) === 1,
      requires_prescription: Number(item.requires_prescription) === 1,
      parent_id: item.parent_id || null
    })
    setShowModal(true)
  }

  const openCreateSubtypeModal = (parentId: number) => {
    if (!canCreateProductType) return
    setEditingId(null)
    reset({ name: '', code: '', description: '', requires_lot: false, requires_prescription: false, parent_id: parentId })
    setShowModal(true)
  }

  // ─── Funciones API (Guardar y Eliminar) ───
  const onSubmit = async (formData: FormValidate) => {
    if (editingId && !canEditProductType) return
    if (!editingId && !canCreateProductType) return

    const payload = {
      ...formData,
      requires_lot: formData.requires_lot ? 1 : 0,
      requires_prescription: formData.requires_prescription ? 1 : 0
    }

    try {
      if (editingId) {
        await productTypeService.updateProductType(editingId, payload)
        toast.success('Tipo de producto actualizado exitosamente')
      } else {
        await productTypeService.createProductType(payload)
        toast.success('Tipo de producto creado exitosamente')
      }
      setShowModal(false)
      setRefreshCount(prev => prev + 1)
    } catch (error) {
      toast.error('Error al guardar el tipo de producto')
    }
  }

  const executeDelete = async () => {
    if (!deleteConfirmId) return
    if (!canDeleteProductType) return
    try {
      await productTypeService.deleteProductType(deleteConfirmId)
      toast.success('Tipo de producto eliminado')
      setRefreshCount(prev => prev + 1)
    } catch (error) {
      toast.error('Error al eliminar')
    } finally {
      setDeleteConfirmId(null)
    }
  }

  // ─── Renderizado (La Vista HTML JSX) ─────────────────────────────────────
  return (
    <div className="space-y-6 p-6">

      {/* 1. Header y Buscador */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tipos de Producto</h1>
          <p className="mt-1 text-sm text-slate-600">Administra los tipos de producto del sistema.</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-64">
            <SearchFilter
              placeholder="Buscar..."
              onSearch={(text) => {
                setSearchQuery(text)
                setCurrentPage(1) // Regresar a la primera página al buscar
              }}
            />
          </div>
          {canCreateProductType && (
            <Button onClick={openCreateModal} className="bg-primary hover:bg-primary/90">
              <Plus className="mr-2 h-4 w-4" /> Nuevo Tipo
            </Button>
          )}
        </div>
      </div>

      {/* 2. Tabla de Datos */}
      {isLoading ? (
        <TableSkeleton
          headers={['Nombre', 'Código', 'Descripción', 'Maneja Lote', 'Requiere Receta', 'Acciones']}
          columns={['text', 'text', 'text', 'badge', 'badge', 'actions']}
          rows={5}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Descripción</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Maneja Lote</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Requiere Receta</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {paginatedData.length > 0 ? (
                  paginatedData.map((item) => (
                    <React.Fragment key={item.id}>
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          <div className="flex items-center gap-2">
                            {item.children && item.children.length > 0 ? (
                              <button onClick={() => toggleRow(item.id)} className="p-1 hover:bg-slate-200 rounded text-slate-500">
                                {expandedRows.includes(item.id) ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                              </button>
                            ) : (
                              <div className="w-6" /> // Placeholder
                            )}
                            {item.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-700">{item.code}</td>
                        <td className="px-6 py-4 text-slate-700">{item.description}</td>
                        <td className="px-6 py-4 text-slate-700">
                          {Number(item.requires_lot) === 1 ? (
                            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">Sí</span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-800">No</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {Number(item.requires_prescription) === 1 ? (
                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">Sí</span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-800">No</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button hidden={!canCreateProductType} onClick={() => openCreateSubtypeModal(item.id)} className="rounded p-1 hover:bg-primary/10" title="Añadir Subtipo">
                              <ListTree className="h-4 w-4 text-blue-500" />
                            </button>
                            <button hidden={!canEditProductType} onClick={() => openEditModal(item)} className="rounded p-1 hover:bg-slate-100" title="Editar">
                              <Edit className="h-4 w-4 text-slate-600" />
                            </button>
                            <button hidden={!canDeleteProductType} onClick={() => setDeleteConfirmId(item.id)} className="rounded p-1 hover:bg-red-50" title="Eliminar">
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Subtypes Row */}
                      {expandedRows.includes(item.id) && item.children && item.children.length > 0 && (
                        <tr className="bg-slate-50/50">
                          <td colSpan={6} className="p-0 border-b border-slate-200">
                            <div className="pl-14 pr-6 py-3 border-l-2 border-green-500 my-2 ml-4">
                              <table className="w-full whitespace-nowrap text-sm">
                                <thead>
                                  <tr>
                                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Subtipo</th>
                                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Código</th>
                                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Descripción</th>
                                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Maneja Lote</th>
                                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Requiere Receta</th>
                                    <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Acciones</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {item.children.map(subtype => (
                                    <tr key={subtype.id} className="hover:bg-slate-100 transition-colors">
                                      <td className="py-3 font-medium text-slate-800 flex items-center gap-2">
                                        {subtype.name}
                                      </td>
                                      <td className="py-3 text-slate-600">{subtype.code}</td>
                                      <td className="py-3 text-slate-600">{subtype.description}</td>
                                      <td className="py-3 text-slate-600">
                                        {Number(subtype.requires_lot) === 1 ? (
                                          <span className="text-green-700 font-medium">Sí</span>
                                        ) : (
                                          <span className="text-slate-500">No</span>
                                        )}
                                      </td>
                                      <td className="py-3 text-slate-600">
                                        {Number(subtype.requires_prescription) === 1 ? (
                                          <span className="text-amber-700 font-medium">Sí</span>
                                        ) : (
                                          <span className="text-slate-500">No</span>
                                        )}
                                      </td>
                                      <td className="py-3">
                                        <div className="flex gap-2">
                                          <button hidden={!canEditProductType} onClick={() => openEditModal(subtype)} className="rounded p-1 hover:bg-white shadow-sm" title="Editar">
                                            <Edit className="h-3.5 w-3.5 text-slate-600" />
                                          </button>
                                          <button hidden={!canDeleteProductType} onClick={() => setDeleteConfirmId(subtype.id)} className="rounded p-1 hover:bg-red-50 shadow-sm" title="Eliminar">
                                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <Package className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">No se encontraron registros</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 3. Paginación */}
          {paginationMeta.total > 0 && (
            <GlobalPaginator
              totalItems={paginationMeta.total}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              from={paginationMeta.from}
              to={paginationMeta.to}
            />
          )}
        </div>
      )}

      {/* 4. Modal Crear/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? (watch('parent_id') ? 'Editar Subtipo' : 'Editar Tipo de Producto')
                : (watch('parent_id') ? 'Nuevo Subtipo' : 'Nuevo Tipo de Producto')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">

            {/* Aviso visual de subtipo */}
            {watch('parent_id') && (
              <div className="bg-[#f0f9f0] border border-[#3B7D3B]/30 text-[#3B7D3B] text-sm p-3 rounded mb-2">
                Vas a crear un <strong>Subtipo</strong> que estará internamente enlazado a su tipo padre.
              </div>
            )}

            {/* Input Nombre */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Nombre <span className="text-red-500">*</span></label>
              <Input {...register('name')} placeholder="Ingresa el nombre" className={errors.name ? 'border-red-500' : ''} />
              {errors.name && <FormError message={errors.name.message} />}
            </div>

            {/* Input Código */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Código <span className="text-red-500">*</span></label>
              <Input {...register('code')} placeholder="Ej. GRUPO_IV" className={errors.code ? 'border-red-500' : ''} />
              {errors.code && <FormError message={errors.code.message} />}
            </div>

            {/* Input Descripción */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Descripción</label>
              <Input {...register('description')} placeholder="Breve descripción" />
              {errors.description && <FormError message={errors.description.message} />}
            </div>

            {/* Checkbox Lote */}
            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                id="requires_lot"
                {...register('requires_lot')}
                className="h-4 w-4 rounded border-slate-300 text-[#3B7D3B] focus:ring-[#3B7D3B]"
              />
              <label htmlFor="requires_lot" className="text-sm font-medium text-slate-700">
                Requiere Lote
              </label>
            </div>
            {errors.requires_lot && <FormError message={errors.requires_lot?.message} />}

            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                id="requires_prescription"
                {...register('requires_prescription')}
                className="h-4 w-4 rounded border-slate-300 text-[#3B7D3B] focus:ring-[#3B7D3B]"
              />
              <label htmlFor="requires_prescription" className="text-sm font-medium text-slate-700">
                Requiere receta médica
              </label>
            </div>
            {errors.requires_prescription && <FormError message={errors.requires_prescription.message} />}

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90">
                {isSubmitting ? <DiscreteLoader /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 5. Modal Global de Confirmación para Eliminar */}
      <ConfirmationModal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={executeDelete}
        preset="delete"
        itemName="tipo de producto"
      />
    </div>
  )
}
